import type { IconName } from "./icons.js";
import { t } from "./i18n.ts";

/** Advanced mode: original 20-tab layout grouped into 4 categories. */
export const TAB_GROUPS = [
  { labelKey: "nav.chat" as const, tabs: ["chat"] },
  {
    labelKey: "nav.dashboard" as const,
    tabs: ["overview", "dashboard", "channels", "sessions", "usage"],
  },
  {
    labelKey: "nav.automation" as const,
    tabs: ["agents", "skills", "cron", "learning", "mind", "spawn-monitor"],
  },
  {
    labelKey: "nav.system" as const,
    tabs: [
      "terminal",
      "files",
      "nodes",
      "instances",
      "logs",
      "activity-log",
      "heartbeat",
      "config",
      "debug",
    ],
  },
] as const;

/**
 * Simple mode: 5 primary tabs for non-technical users.
 * Each entry has a main tab and optional child tabs accessible via a submenu.
 */
export type SimpleTabEntry = {
  tab: Tab;
  labelKey: string;
  icon: IconName;
  children?: Tab[];
};

export const SIMPLE_TABS: SimpleTabEntry[] = [
  { tab: "chat", labelKey: "nav.simple.chat", icon: "messageSquare" },
  {
    tab: "dashboard",
    labelKey: "nav.simple.status",
    icon: "radio",
    children: ["overview", "channels", "sessions", "usage", "instances"],
  },
  {
    tab: "config",
    labelKey: "nav.simple.settings",
    icon: "settings",
    children: ["agents"],
  },
  {
    tab: "skills",
    labelKey: "nav.simple.tools",
    icon: "zap",
    children: [
      "terminal",
      "files",
      "nodes",
      "cron",
      "learning",
      "mind",
      "logs",
      "activity-log",
      "debug",
      "spawn-monitor",
    ],
  },
  { tab: "learning", labelKey: "nav.simple.help", icon: "zap" },
];

export type Tab =
  | "agents"
  | "overview"
  | "dashboard"
  | "channels"
  | "instances"
  | "sessions"
  | "usage"
  | "cron"
  | "skills"
  | "nodes"
  | "chat"
  | "config"
  | "debug"
  | "logs"
  | "terminal"
  | "files"
  | "learning"
  | "mind"
  | "activity-log"
  | "heartbeat"
  | "spawn-monitor";

const TAB_PATHS: Record<Tab, string> = {
  agents: "/agents",
  overview: "/overview",
  dashboard: "/dashboard",
  channels: "/channels",
  instances: "/instances",
  sessions: "/sessions",
  usage: "/usage",
  cron: "/cron",
  skills: "/skills",
  nodes: "/nodes",
  chat: "/chat",
  config: "/config",
  debug: "/debug",
  logs: "/logs",
  terminal: "/terminal",
  files: "/files",
  learning: "/learning",
  mind: "/mind",
  "activity-log": "/activity-log",
  heartbeat: "/heartbeat",
  "spawn-monitor": "/spawn-monitor",
};

const PATH_TO_TAB = new Map(Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]));

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    return "chat";
  }
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "overview":
      return "barChart";
    case "dashboard":
      return "radio";
    case "channels":
      return "link";
    case "instances":
      return "radio";
    case "sessions":
      return "fileText";
    case "usage":
      return "barChart";
    case "cron":
      return "loader";
    case "skills":
      return "zap";
    case "nodes":
      return "monitor";
    case "terminal":
      return "monitor";
    case "files":
      return "folder";
    case "learning":
      return "zap";
    case "mind":
      return "zap";
    case "config":
      return "settings";
    case "debug":
      return "bug";
    case "logs":
      return "scrollText";
    case "activity-log":
      return "scrollText";
    case "heartbeat":
      return "radio";
    case "spawn-monitor":
      return "radio";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  return t(`tab.${tab}` as any) || tab;
}

export function subtitleForTab(tab: Tab) {
  return t(`sub.${tab}` as any) || "";
}
